import React, { useEffect, useMemo, useRef, useState } from 'react';
import i18next from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import numeral from 'numeral';
import moment from 'moment';

import {
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  message as antMessage
} from 'antd';

import { useParams } from 'react-router-dom';
import useQuotesContext from './QuotesContext';
import useAuthContext from '../../contexts/AuthContext';

import {
  StandardIcon,
  StandingIcon,
  UnfurnishedIcon,
  FurnishedIcon,
  ApartmentIcon,
  OtherBuildingIcon,
  UnoccupiedIcon,
  ConstructionIcon,
  ConstructionLongIcon,
  MachineBreakdownIcon,
  SolarPanelIcon,
  VerandaIcon,
  OtherIcon
} from '../../utils/constants/formIcons';
import useErrorMessage from '../../utils/ErrorMessage';
import { arrayConditionnalElement } from '../../utils/arrayConditionnalElement';
import FormRadioButtonWithCustom from '../../components/FormRadioButtonWithCustom/FormRadioButtonWithCustom';
import PreRequisitesSwitch from './PreRequisitesSwitch';
import { formatPrice } from '../../utils/price';
import { amendmentPurposes, editPurposes } from './QuotesRouter';
import {
  inputNumberFormatter,
  inputNumberParser
} from '../../utils/inputNumberFormatter';
import { AddressAutocomplete } from '../../components/AddressAutocomplete/AddressAutocomplete';
import { preRequisitesLayout } from '../../utils/constants/formLayout';
import { ComparisonTable } from '../../components/ComparisonTable/ComparisonTable';
import { removeAccents } from '../../utils/string';
import { isMetropolitanZipcode, isValidZipcode } from '../../utils/zipcode';
import MriwkTREModal from './MriwkTREModal';

export const MRIWK_BUILDING_COUNT_EXEMPTION_LIMIT = 4;
export const MRIWK_BUILDING_COUNT_DEFAULT_VALUE = 1;

const buildingTypes = [
  {
    value: 'BUILDING',
    icon: <StandardIcon />,
    label: i18next.t('contracts.tags.NOOFGPRICING1_RENTAL_BUILDING')
  },
  // {
  //   value: 'HOUSE',
  //   icon: <HouseIcon />,
  //   label: i18next.t('contracts.tags.NOOFGPRICING1_HOUSE')
  // },
  {
    value: 'APARTMENT',
    icon: <ApartmentIcon />,
    label: i18next.t('contracts.tags.NOOFGPRICING1_APARTMENT')
  },
  {
    value: 'OTHER',
    icon: <OtherBuildingIcon />,
    label: i18next.t('contracts.tags.OTHER')
  }
];
export const MRIWK_BUILDING_TYPES_EXEMPTION = ['OTHER', 'HOUSE'];
export const MRIWK_BUILDING_TYPES_DEFAULT_VALUE = buildingTypes[0].value;
const pnoBuildingTypes = ['HOUSE', 'APARTMENT'];
const mriBuildingTypes = ['BUILDING'];
export const MRIWK_PNO_BUILDING_TYPES = pnoBuildingTypes;
export const MRIWK_MRI_BUILDING_TYPES = mriBuildingTypes;

export const isWKPno = ({ test, building_type = null } = {}) =>
  pnoBuildingTypes.includes(building_type);
export const isWKMri = ({ building_type = null } = {}) =>
  mriBuildingTypes.includes(building_type);

const buildingQualities = [
  {
    value: 'STANDARD',
    icon: <StandardIcon />,
    label: i18next.t('contracts.tags.STANDARD')
  },
  {
    value: 'STANDING',
    icon: <StandingIcon />,
    label: i18next.t('contracts.tags.STANDING')
  }
];
export const MRIWK_BUILDING_QUALITY_DEFAULT_VALUE = buildingQualities[0].value;

const propertyNatures = [
  {
    value: 'VERTICAL',
    label: i18next.t('contracts.form.property_nature_values.vertical')
  },
  {
    value: 'HORIZONTAL',
    label: i18next.t('contracts.form.property_nature_values.horizontal')
  },
  {
    value: 'ASL_AFUL',
    label: i18next.t('contracts.form.property_nature_values.asl_aful')
  }
];
const mriPropertyNatures = propertyNatures.filter(
  ({ value }) => value !== 'ASL_AFUL'
);
const pnoPropertyNatures = propertyNatures;
export const MRIWK_PROPERTY_NATURE_DEFAULT_VALUE = propertyNatures[0].value;
export const MRIWK_PROPERTY_NATURE_EXEMPTION_VALUES = ['ASL_AFUL'];

export const MRIWK_PNO_SURFACE_EXEMPTION_LIMIT_MIN = 0;
export const MRIWK_PNO_SURFACE_EXEMPTION_LIMIT_MAX = 200;
export const MRIWK_MRI_SURFACE_EXEMPTION_LIMIT_MIN = 200;
export const MRIWK_MRI_SURFACE_EXEMPTION_LIMIT_MAX = 2000;
export const MRIWK_MRI_SURFACE_SUBSCRIPTION_LIMIT_MAX = 8000;
export const MRIWK_MRI_SURFACE_DEFAULT_VALUE = 200;
export const MRIWK_PNO_SURFACE_DEFAULT_VALUE = 100;
export const MRIWK_SURFACE_DEFAULT_VALUE = MRIWK_MRI_SURFACE_DEFAULT_VALUE;

const vacantReason = [
  {
    value: 'EMPTY',
    icon: <UnoccupiedIcon />,
    label: i18next.t('contracts.form.vacant_details.empty')
  },
  {
    value: 'IN_WORK_SHORT',
    icon: <ConstructionIcon />,
    label: i18next.t('contracts.form.vacant_details.in_work_short')
  },
  {
    value: 'IN_WORK_LONG',
    icon: <ConstructionLongIcon />,
    label: i18next.t('contracts.form.vacant_details.in_work_long')
  }
];
export const MRIWK_VACANT_REASON_DEFAULT_VALUE = vacantReason[0].value;

const vacantArea = [
  {
    value: 'MORE_25',
    label: i18next.t('contracts.form.vacant_area.more_25')
  },
  {
    value: 'MORE_50',
    label: i18next.t('contracts.form.vacant_area.more_50')
  },
  {
    value: 'TOTAL',
    label: i18next.t('contracts.form.vacant_area.total')
  }
];

const deadline_before_rental = [
  {
    value: 'LESS_THAN_3_MONTHS',
    label: i18next.t('contracts.form.deadline_before_rental.LESS_THAN_3_MONTHS')
  },
  {
    value: 'MORE_THAN_3_MONTHS',
    label: i18next.t('contracts.form.deadline_before_rental.MORE_THAN_3_MONTHS')
  }
];

export const MRIWK_VACANT_AREA_DEFAULT_VALUE = vacantArea[0].value;
export const MRIWK_DEADLINE_BEFORE_RENTAL_DEFAULT_VALUE =
  deadline_before_rental[0].value;

const officeActivityPecentages = [
  {
    value: 'NONE',
    label: i18next.t('contracts.form.office_activity_percentage_none')
  },
  {
    value: 'FEWER_50',
    label: i18next.t('contracts.form.office_activity_percentage_range', {
      min: 1,
      max: 50
    })
  },
  {
    value: 'MORE_50',
    label: i18next.t('contracts.form.office_activity_percentage_more', {
      percentage: 50
    })
  }
];
export const MRIWK_OFFICE_ACTIVITY_PERCENTAGE_DEFAULT_VALUE = 0;
export const MRIWK_OFFICE_ACTIVITY_PERCENTAGE_EXEMPTION = 50;

const shopActivityPecentages = [
  {
    value: 'NONE',
    label: i18next.t('contracts.form.shop_activity_percentage_none')
  },
  {
    value: 'FEWER_25',
    label: i18next.t('contracts.form.shop_activity_percentage_range', {
      min: 1,
      max: 25
    })
  },
  {
    value: 'MORE_25',
    label: i18next.t('contracts.form.shop_activity_percentage_more', {
      percentage: 25
    })
  }
];
export const MRIWK_SHOP_ACTIVITY_PERCENTAGE_DEFAULT_VALUE = 0;
export const MRIWK_SHOP_ACTIVITY_PERCENTAGE_EXEMPTION = 25;

export const MRIWK_SHOP_AND_OFFICE_ACTIVITY_PERCENTAGE_EXEMPTION = 50;

export const MRIWK_OUTBUILDING_EXEMPTION_LIMIT = 100;
const outbuildingSurfaceSteps = [
  {
    value: 20,
    label: i18next.t('contracts.form.outbuilding_surface_fewer', {
      surface: 20
    })
  },
  {
    value: 50,
    label: i18next.t('contracts.form.outbuilding_surface_range', {
      min: 21,
      max: 50
    })
  },
  {
    value: 100,
    label: i18next.t('contracts.form.outbuilding_surface_range', {
      min: 51,
      max: 100
    })
  },
  {
    value: MRIWK_OUTBUILDING_EXEMPTION_LIMIT + 1,
    label: i18next.t('contracts.form.outbuilding_surface_exceed', {
      surface: MRIWK_OUTBUILDING_EXEMPTION_LIMIT
    })
  }
];
export const MRIWK_OUTBUILDING_SURFACE_DEFAULT_VALUE =
  outbuildingSurfaceSteps[0].value;

export const MRIWK_FURNITURE_GUARANTEE_EXEMPTION_LIMIT = 20000;
const furnitureGuaranteeSteps = [
  {
    value: 10000,
    label: formatPrice(10000, true)
  },
  {
    value: 20000,
    label: formatPrice(20000, true)
  }
  // {
  //   value: MRIWK_FURNITURE_GUARANTEE_EXEMPTION_LIMIT + 1,
  //   label: i18next.t('contracts.form.furniture_guarantee_exceed', {
  //     furniture_guarantee: formatPrice(
  //       MRIWK_FURNITURE_GUARANTEE_EXEMPTION_LIMIT
  //     )
  //   })
  // }
];
export const MRIWK_FURNITURE_GUARANTEE_DEFAULT_VALUE =
  furnitureGuaranteeSteps[0].value;

export const MRIWK_CLAIM_AMOUNT_EXEMPTION_THRESHOLD = 1500;
export const MRIWK_CLAIM_NUMBER_EXEMPTION_THRESHOLD = 1;

const ighIgsValues = [
  {
    value: 'NONE',
    label: i18next.t('contracts.form.igh_igs_values.none')
    // icon: <NoIcon />
  },
  {
    value: 'IGH',
    label: i18next.t('contracts.form.igh_igs_values.igh')
    // icon: <IghIcon />
  },
  {
    value: 'IGS',
    label: i18next.t('contracts.form.igh_igs_values.igs')
    // icon: <IgsIcon />
  }
];
export const MRIWK_IGH_IGS_VALUES_DEFAULT_VALUE = ighIgsValues[0].value;
export const MRIWK_IGH_IGS_EXEMPTION_VALUES = ['IGH', 'IGS'];

const propertyAltitude = [
  {
    value: 'FEWER_1500',
    label: i18next.t('contracts.form.property_altitude_values.fewer', {
      altitude: 1500
    })
  },
  {
    value: 'MORE_1500',
    label: i18next.t('contracts.form.property_altitude_values.more', {
      altitude: 1500
    })
  }
];
export const MRIWK_PROPERTY_ALTITUDE_DEFAULT_VALUE = propertyAltitude[0].value;

const propertyManagersMap = {
  MONO: 'MONO',
  SYNDIC_PRO: 'SYNDIC_PRO',
  SYNDYC_VOLUNTEER: 'SYNDYC_VOLUNTEER',
  COMPANY: 'COMPANY'
};

const propertyManagers = [
  {
    value: 'MONO',
    label: i18next.t('contracts.form.property_managers_values.mono')
  },
  {
    value: 'SYNDIC_PRO',
    label: i18next.t('contracts.form.property_managers_values.syndic_pro')
  },
  {
    value: 'SYNDYC_VOLUNTEER',
    label: i18next.t('contracts.form.property_managers_values.syndic_volunteer')
  },
  {
    value: 'COMPANY',
    label: i18next.t('contracts.form.property_managers_values.syndic_volunteer')
  }
  // {
  //   value: 'CO_OWNER_FEWER_50',
  //   label: i18next.t('contracts.form.property_managers_values.co_owner_fewer', {
  //     percentage: 50
  //   })
  // },
  // {
  //   value: 'CO_OWNER_MORE_50',
  //   label: i18next.t('contracts.form.property_managers_values.co_owner_more', {
  //     percentage: 50
  //   })
  // }
];
export const MRIWK_PROPERTY_MANAGER_DEFAULT_VALUE = propertyManagers[0].value;

const deductibleAmountRatios = [
  {
    value: 0,
    label: i18next.t('contracts.form.deductible_amount_ratio_values', {
      ratio: 0
    })
  },
  {
    value: 0.25,
    label: i18next.t('contracts.form.deductible_amount_ratio_values', {
      ratio: 0.25
    })
  },
  {
    value: 0.5,
    label: i18next.t('contracts.form.deductible_amount_ratio_values', {
      ratio: 0.5
    })
  },
  {
    value: 0.75,
    label: i18next.t('contracts.form.deductible_amount_ratio_values', {
      ratio: 0.75
    })
  },
  {
    value: 1,
    label: i18next.t('contracts.form.deductible_amount_ratio_values', {
      ratio: 1
    })
  },
  {
    value: 1.5,
    label: i18next.t('contracts.form.deductible_amount_ratio_values', {
      ratio: 1.5
    })
  },
  {
    value: 3,
    label: i18next.t('contracts.form.deductible_amount_ratio_values', {
      ratio: 3
    })
  },
  {
    value: 4.5,
    label: i18next.t('contracts.form.deductible_amount_ratio_values', {
      ratio: 4.5
    })
  }
];
export const MRIWK_DEDUCTIBLE_AMOUNT_RATIO_DEFAULT_VALUE = 1;
export const MRIWK_DEDUCTIBLE_AMOUNT_RATIO_EXEMPTION_VALUES = [];

const guaranteePackages = [
  {
    value: 'PKG_1',
    label: i18next.t('contracts.form.guarantee_package_values', {
      package_number: 1
    })
  },
  {
    value: 'PKG_2',
    label: i18next.t('contracts.form.guarantee_package_values', {
      package_number: 2
    })
  },
  {
    value: 'PKG_3',
    label: i18next.t('contracts.form.guarantee_package_values', {
      package_number: 3
    })
  }
];
export const MRIWK_GUARANTEE_PACKAGE_DEFAULT_VALUE = guaranteePackages[1].value;

export const MRIWKOptionnalGuaranteesMap = {
  machine_breakdown: 'MACHINE_BREAKDOWN',
  indoor_pool: 'INDOOR_POOL',
  outdoor_pool: 'OUTDOOR_POOL',
  solar_panels: 'SOLAR_PANELS',
  veranda: 'VERANDA'
};

const optionnalGuarantees = [
  {
    value: MRIWKOptionnalGuaranteesMap.machine_breakdown,
    label: i18next.t('contracts.form.machine_breakdown'),
    icon: <MachineBreakdownIcon />
  },
  // {
  //   value: MRIWKOptionnalGuaranteesMap.indoor_pool,
  //   label: i18next.t('contracts.form.indoor_pool'),
  //   icon: <IndoorPoolIcon />
  // },
  // {
  //   value: MRIWKOptionnalGuaranteesMap.outdoor_pool,
  //   label: i18next.t('contracts.form.outdoor_pool'),
  //   icon: <OutdoorPoolIcon />
  // },
  {
    value: MRIWKOptionnalGuaranteesMap.solar_panels,
    label: i18next.t('contracts.form.solar_panels'),
    icon: <SolarPanelIcon />
  },
  {
    value: MRIWKOptionnalGuaranteesMap.veranda,
    label: i18next.t('contracts.form.veranda'),
    icon: <VerandaIcon />
  }
];
export const MRIWK_OPTIONNAL_GUARANTEES_DEFAULT_VALUE = [];

const woodFloorValues = [
  {
    value: 'NO',
    label: i18next.t('contracts.form.wood_floor_values.no')
  },
  {
    value: 'YES',
    label: i18next.t('contracts.form.wood_floor_values.yes')
  },
  {
    value: 'UNKNOWN',
    label: i18next.t('contracts.form.wood_floor_values.unknown')
  }
];
export const MRIWK_WOOD_FLOOR_DEFAULT_VALUE = woodFloorValues[0].value;

const claimTypes = [
  {
    value: 'ATTACK',
    label: i18next.t('contracts.form.mriwk_claim_type.ATTACK')
  },
  {
    value: 'BROKEN_GLASS',
    label: i18next.t('contracts.form.mriwk_claim_type.BROKEN_GLASS')
  },
  {
    value: 'BROKEN_MATERIAL',
    label: i18next.t('contracts.form.mriwk_claim_type.BROKEN_MATERIAL')
  },
  {
    value: 'NATURAL_DISASTER',
    label: i18next.t('contracts.form.mriwk_claim_type.NATURAL_DISASTER')
  },
  {
    value: 'TECHNOLOGICAL_DISASTER',
    label: i18next.t('contracts.form.mriwk_claim_type.TECHNOLOGICAL_DISASTER')
  },
  {
    value: 'UNPAID_SYNDIC_FEES',
    label: i18next.t('contracts.form.mriwk_claim_type.UNPAID_SYNDIC_FEES')
  },
  {
    value: 'DPRSA',
    label: i18next.t('contracts.form.mriwk_claim_type.DPRSA')
  },
  {
    value: 'WATER_DAMAGE',
    label: i18next.t('contracts.form.mriwk_claim_type.WATER_DAMAGE')
  },
  {
    value: 'BUILDING_DAMAGE',
    label: i18next.t('contracts.form.mriwk_claim_type.BUILDING_DAMAGE')
  },
  {
    value: 'OUTDOOR_DAMAGE',
    label: i18next.t('contracts.form.mriwk_claim_type.OUTDOOR_DAMAGE')
  },
  {
    value: 'ELECTRICAL_DAMAGE',
    label: i18next.t('contracts.form.mriwk_claim_type.ELECTRICAL_DAMAGE')
  },
  {
    value: 'COLLAPSE',
    label: i18next.t('contracts.form.mriwk_claim_type.COLLAPSE')
  },
  {
    value: 'FEES_LOSSES',
    label: i18next.t('contracts.form.mriwk_claim_type.FEES_LOSSES')
  },
  {
    value: 'GAREAT',
    label: i18next.t('contracts.form.mriwk_claim_type.GAREAT')
  },
  {
    value: 'GRAFFITIS',
    label: i18next.t('contracts.form.mriwk_claim_type.GRAFFITIS')
  },
  {
    value: 'SYNDIC_CLAIMS_FEES',
    label: i18next.t('contracts.form.mriwk_claim_type.SYNDIC_CLAIMS_FEES')
  },
  {
    value: 'FIRE',
    label: i18next.t('contracts.form.mriwk_claim_type.FIRE')
  },
  {
    value: 'CURTAIN_WALLS_VERANDA',
    label: i18next.t('contracts.form.mriwk_claim_type.CURTAIN_WALLS_VERANDA')
  },
  {
    value: 'SOLAR_PANEL',
    label: i18next.t('contracts.form.mriwk_claim_type.SOLAR_PANEL')
  },
  {
    value: 'LEGAL_PROTECTION',
    label: i18next.t('contracts.form.mriwk_claim_type.LEGAL_PROTECTION')
  },
  {
    value: 'CIVIL_PROTECTION',
    label: i18next.t('contracts.form.mriwk_claim_type.CIVIL_PROTECTION')
  },
  {
    value: 'BROKEN_VESSEL',
    label: i18next.t('contracts.form.mriwk_claim_type.BROKEN_VESSEL')
  },
  {
    value: 'STORM_SNOW',
    label: i18next.t('contracts.form.mriwk_claim_type.STORM_SNOW')
  },
  {
    value: 'THEFT',
    label: i18next.t('contracts.form.mriwk_claim_type.THEFT')
  }
];

const prerequisites = [
  {
    name: 'hard_material_prerequisite',
    common: true
  },
  {
    name: 'good_state_prerequisite',
    common: true
  },
  {
    name: 'firefighting_water_prerequisite',
    common: true
  },
  {
    name: 'rescue_access_prerequisite',
    common: true
  },
  {
    name: 'metropolitan_france_prerequisite',
    common: true
  },
  {
    name: 'public_late_prerequisite',
    pno: false,
    mri: true
  },
  {
    name: 'is_asl_aful_prerequisite',
    pno: false,
    mri: true
  },
  {
    name: 'fast_food_prerequisite',
    pno: false,
    mri: true
  },
  {
    name: 'temporary_activity_prerequisite',
    pno: false,
    mri: true
  },
  {
    name: 'recreation_activity_prerequisite',
    pno: false,
    mri: true
  },
  {
    name: 'political_activity_prerequisite',
    pno: false,
    mri: true
  },
  {
    name: 'industrial_activity_prerequisite',
    pno: false,
    mri: true
  },
  {
    name: 'transformed_products_prerequisite',
    pno: false,
    mri: true
  },
  {
    name: 'cold_room_prerequisite',
    pno: false,
    mri: true
  },
  {
    name: 'unbuildable_prerequisite',
    pno: false,
    mri: true
  },
  {
    name: 'roof_prerequisite',
    common: true
  },
  {
    name: 'agricultural_building_prerequisite',
    common: true
  },
  {
    name: 'igh_igs_prerequisite',
    common: true
  },
  {
    name: 'peril_prerequisite',
    common: true
  },
  {
    name: 'vacant_prequisite',
    common: true
  },
  {
    name: 'demolition_prerequisite',
    common: true
  },
  {
    name: 'zup_prerequisite',
    common: true
  },
  {
    name: 'flooding_prerequisite',
    common: true
  },
  {
    name: 'condominium_surface_prerequisite',
    common: true
  },
  {
    name: 'classified_prerequisite',
    common: true
  },
  {
    name: 'monument_prerequisite',
    common: true
  },
  {
    name: 'liquidation_prerequisite',
    common: true
  },
  {
    name: 'near_industrial_activity_prerequisite',
    common: true
  },
  {
    name: 'waiver_clause_prerequisite',
    common: true
  }
];

const mriwkTerminationReasons = [
  {
    value: 'UNPAID',
    label: i18next.t('contracts.form.UNPAID')
  },
  {
    value: 'CLAIMS',
    label: i18next.t('contracts.form.CLAIMS')
  },
  {
    value: 'FALSE_DECLARATION',
    label: i18next.t('contracts.form.FALSE_DECLARATION')
  },
  {
    value: 'OTHER',
    label: i18next.t('contracts.form.OTHER')
  }
];

export const MRIWK_FURNISHED_EXEMPTION_VALUES = ['OTHER'];

export const MRIWK_SUBSCRIBER_TYPE_EXEMPTION_VALUES = ['OTHER'];

const addressValidator = (address = {}) => {
  if (!address) {
    return false;
  }

  const { number, street, postal_code, country = 'France', city } = address;

  return (
    !!number &&
    !!street &&
    !!postal_code &&
    !!country &&
    !!city &&
    !!postal_code &&
    postal_code.length >= 5
  );
};

const MRIguaranteePackageComparisonDataSource = [
  {
    title: 'Formule 1',
    key: 'PKG_1',
    data: {
      g1231_property_owner_civil_liability: {
        label: i18next.t('contracts.form.g1231_property_owner_civil_liability'),
        value: true
      },
      g1231_criminal_defense_recourse_following_accident: {
        label: i18next.t(
          'contracts.form.g1231_criminal_defense_recourse_following_accident'
        ),
        value: true
      },
      g1231_natural_disasters: {
        label: i18next.t('contracts.form.g1231_natural_disasters'),
        value: true
      },
      g1231_juridic_protection: {
        label: i18next.t('contracts.form.g1231_juridic_protection'),
        value: true
      },
      g1231_technological_risks: {
        label: i18next.t('contracts.form.g1231_technological_risks'),
        value: true
      },
      g1231_fire_related_risks: {
        label: i18next.t('contracts.form.g1231_fire_related_risks'),
        value: true
      },
      g1231_attacks: {
        label: i18next.t('contracts.form.g1231_attacks'),
        value: true
      },
      g1231_gareat: {
        label: i18next.t('contracts.form.g1231_gareat'),
        value: true
      },
      g1231_union_civil_liability: {
        label: i18next.t('contracts.form.g1231_union_civil_liability'),
        value: true
      }
    }
  },
  {
    title: 'Formule 2',
    key: 'PKG_2',
    data: {
      g1231_property_owner_civil_liability: {
        label: i18next.t('contracts.form.g1231_property_owner_civil_liability'),
        value: true
      },
      g1231_criminal_defense_recourse_following_accident: {
        label: i18next.t(
          'contracts.form.g1231_criminal_defense_recourse_following_accident'
        ),
        value: true
      },
      g1231_natural_disasters: {
        label: i18next.t('contracts.form.g1231_natural_disasters'),
        value: true
      },
      g1231_juridic_protection: {
        label: i18next.t('contracts.form.g1231_juridic_protection'),
        value: true
      },
      g1231_technological_risks: {
        label: i18next.t('contracts.form.g1231_technological_risks'),
        value: true
      },
      g1231_fire_related_risks: {
        label: i18next.t('contracts.form.g1231_fire_related_risks'),
        value: true
      },
      g1231_attacks: {
        label: i18next.t('contracts.form.g1231_attacks'),
        value: true
      },
      g1231_gareat: {
        label: i18next.t('contracts.form.g1231_gareat'),
        value: true
      },
      g1231_union_civil_liability: {
        label: i18next.t('contracts.form.g1231_union_civil_liability'),
        value: true
      },

      g0230_storms_hail_heavy_snow: {
        label: i18next.t('contracts.form.g0230_storms_hail_heavy_snow'),
        value: true
      },
      g0230_collapse: {
        label: i18next.t('contracts.form.g0230_collapse'),
        value: true
      },
      g0230_theft: {
        label: i18next.t('contracts.form.g0230_theft'),
        value: true
      },
      g0230_electrical_damage: {
        label: i18next.t('contracts.form.g0230_electrical_damage'),
        value: true
      }
    }
  },
  {
    title: 'Formule 3',
    key: 'PKG_3',
    data: {
      g1231_property_owner_civil_liability: {
        label: i18next.t('contracts.form.g1231_property_owner_civil_liability'),
        value: true
      },
      g1231_criminal_defense_recourse_following_accident: {
        label: i18next.t(
          'contracts.form.g1231_criminal_defense_recourse_following_accident'
        ),
        value: true
      },
      g1231_natural_disasters: {
        label: i18next.t('contracts.form.g1231_natural_disasters'),
        value: true
      },
      g1231_juridic_protection: {
        label: i18next.t('contracts.form.g1231_juridic_protection'),
        value: true
      },
      g1231_technological_risks: {
        label: i18next.t('contracts.form.g1231_technological_risks'),
        value: true
      },
      g1231_fire_related_risks: {
        label: i18next.t('contracts.form.g1231_fire_related_risks'),
        value: true
      },
      g1231_attacks: {
        label: i18next.t('contracts.form.g1231_attacks'),
        value: true
      },
      g1231_gareat: {
        label: i18next.t('contracts.form.g1231_gareat'),
        value: true
      },
      g1231_union_civil_liability: {
        label: i18next.t('contracts.form.g1231_union_civil_liability'),
        value: true
      },

      g0230_storms_hail_heavy_snow: {
        label: i18next.t('contracts.form.g0230_storms_hail_heavy_snow'),
        value: true
      },
      g0230_collapse: {
        label: i18next.t('contracts.form.g0230_collapse'),
        value: true
      },
      g0230_theft: {
        label: i18next.t('contracts.form.g0230_theft'),
        value: true
      },
      g0230_electrical_damage: {
        label: i18next.t('contracts.form.g0230_electrical_damage'),
        value: true
      },

      g0030_broken_glass: {
        label: i18next.t('contracts.form.g0030_broken_glass'),
        value: true
      },
      g0030_water_damage: {
        label: i18next.t('contracts.form.g0030_water_damage'),
        value: true
      },
      g0030_vandalism: {
        label: i18next.t('contracts.form.g0030_vandalism'),
        value: true
      }
    }
  }
];

const PNOguaranteePackageComparisonDataSource = [
  {
    title: 'Formule 1',
    key: 'PKG_1',
    data: {
      g1231_property_owner_civil_liability: {
        label: i18next.t('contracts.form.g1231_property_owner_civil_liability'),
        value: true
      },
      g1231_criminal_defense_recourse_following_accident: {
        label: i18next.t(
          'contracts.form.g1231_criminal_defense_recourse_following_accident'
        ),
        value: true
      },
      g1231_natural_disasters: {
        label: i18next.t('contracts.form.g1231_natural_disasters'),
        value: true
      },
      g1231_juridic_protection: {
        label: i18next.t('contracts.form.g1231_juridic_protection'),
        value: true
      },
      g1231_technological_risks: {
        label: i18next.t('contracts.form.g1231_technological_risks'),
        value: true
      },
      g1231_fire_related_risks: {
        label: i18next.t('contracts.form.g1231_fire_related_risks'),
        value: true
      },
      g1231_attacks: {
        label: i18next.t('contracts.form.g1231_attacks'),
        value: true
      },
      g1231_gareat: {
        label: i18next.t('contracts.form.g1231_gareat'),
        value: true
      },
      g1231_union_civil_liability: {
        label: i18next.t('contracts.form.g1231_union_civil_liability'),
        value: true
      }
    }
  },
  {
    title: 'Formule 2',
    key: 'PKG_2',
    data: {
      g1231_property_owner_civil_liability: {
        label: i18next.t('contracts.form.g1231_property_owner_civil_liability'),
        value: true
      },
      g1231_criminal_defense_recourse_following_accident: {
        label: i18next.t(
          'contracts.form.g1231_criminal_defense_recourse_following_accident'
        ),
        value: true
      },
      g1231_natural_disasters: {
        label: i18next.t('contracts.form.g1231_natural_disasters'),
        value: true
      },
      g1231_juridic_protection: {
        label: i18next.t('contracts.form.g1231_juridic_protection'),
        value: true
      },
      g1231_technological_risks: {
        label: i18next.t('contracts.form.g1231_technological_risks'),
        value: true
      },
      g1231_fire_related_risks: {
        label: i18next.t('contracts.form.g1231_fire_related_risks'),
        value: true
      },
      g1231_attacks: {
        label: i18next.t('contracts.form.g1231_attacks'),
        value: true
      },
      g1231_gareat: {
        label: i18next.t('contracts.form.g1231_gareat'),
        value: true
      },
      g1231_union_civil_liability: {
        label: i18next.t('contracts.form.g1231_union_civil_liability'),
        value: true
      },

      g0230_storms_hail_heavy_snow: {
        label: i18next.t('contracts.form.g0230_storms_hail_heavy_snow'),
        value: true
      },
      g0230_collapse: {
        label: i18next.t('contracts.form.g0230_collapse'),
        value: true
      },
      g0230_theft: {
        label: i18next.t('contracts.form.g0230_theft'),
        value: true
      },
      g0230_electrical_damage: {
        label: i18next.t('contracts.form.g0230_electrical_damage'),
        value: true
      }
    }
  },
  {
    title: 'Formule 3',
    key: 'PKG_3',
    data: {
      g1231_property_owner_civil_liability: {
        label: i18next.t('contracts.form.g1231_property_owner_civil_liability'),
        value: true
      },
      g1231_criminal_defense_recourse_following_accident: {
        label: i18next.t(
          'contracts.form.g1231_criminal_defense_recourse_following_accident'
        ),
        value: true
      },
      g1231_natural_disasters: {
        label: i18next.t('contracts.form.g1231_natural_disasters'),
        value: true
      },
      g1231_juridic_protection: {
        label: i18next.t('contracts.form.g1231_juridic_protection'),
        value: true
      },
      g1231_technological_risks: {
        label: i18next.t('contracts.form.g1231_technological_risks'),
        value: true
      },
      g1231_fire_related_risks: {
        label: i18next.t('contracts.form.g1231_fire_related_risks'),
        value: true
      },
      g1231_attacks: {
        label: i18next.t('contracts.form.g1231_attacks'),
        value: true
      },
      g1231_gareat: {
        label: i18next.t('contracts.form.g1231_gareat'),
        value: true
      },
      g1231_union_civil_liability: {
        label: i18next.t('contracts.form.g1231_union_civil_liability'),
        value: true
      },

      g0230_storms_hail_heavy_snow: {
        label: i18next.t('contracts.form.g0230_storms_hail_heavy_snow'),
        value: true
      },
      g0230_collapse: {
        label: i18next.t('contracts.form.g0230_collapse'),
        value: true
      },
      g0230_theft: {
        label: i18next.t('contracts.form.g0230_theft'),
        value: true
      },
      g0230_electrical_damage: {
        label: i18next.t('contracts.form.g0230_electrical_damage'),
        value: true
      },

      g0030_broken_glass: {
        label: i18next.t('contracts.form.g0030_broken_glass'),
        value: true
      },
      g0030_water_damage: {
        label: i18next.t('contracts.form.g0030_water_damage'),
        value: true
      },
      g0030_vandalism: {
        label: i18next.t('contracts.form.g0030_vandalism'),
        value: true
      }
    }
  }
];

const useMRIWKFields = (form, purpose, currentStep) => {
  const { subPath: type } = useParams();
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const { t } = useTranslation();
  const {
    formValues,
    setFormValues,
    exemptions,
    setEstimationAvoider,
    estimatedCost,
    dataModal,
    setEstimatedCost
  } = useQuotesContext();
  const [isInitialized, setIsInitialized] = useState(false);
  const [cities, setCities] = useState([]);
  const [manualAddress, setManualAddress] = useState(
    editPurposes.includes(purpose)
  );
  const [addressAdditional, setAddressAdditional] = useState(false);
  const [surfaceFieldInitialized, setSurfaceFieldInitialized] = useState(false);
  const [treModalVisible, setTreModalVisible] = useState(false);

  const isEdit = useMemo(() => editPurposes.includes(purpose), [purpose]);

  const addressWatcher = Form.useWatch(['address'], form);
  const postalCodeWatcher = Form.useWatch(['address', 'postal_code'], form);
  const isBuildingTypeTouched = form.isFieldTouched('building_type');

  const addressAutocompleteRef = useRef(null);

  const isPNO = useMemo(
    () => pnoBuildingTypes.includes(formValues?.building_type),
    [formValues?.building_type]
  );

  const isMRI = useMemo(
    () => mriBuildingTypes.includes(formValues?.building_type),
    [formValues?.building_type]
  );

  const addressIsValid = useMemo(() => {
    if (!addressWatcher) {
      return false;
    }

    return addressValidator(addressWatcher);
  }, [addressWatcher]);

  const prerequisiteFields = useMemo(
    () =>
      prerequisites
        .reduce((acc, prerequisite) => {
          if (prerequisite.common) {
            acc.push(prerequisite.name);
          } else if (prerequisite.pno && isPNO) {
            acc.push(prerequisite.name);
          } else if (prerequisite.mri && isMRI) {
            acc.push(prerequisite.name);
          }

          return acc;
        }, [])
        .map((fieldName) => ({
          name: [fieldName],
          labelAlign: 'left',
          formattedLabel: true,
          label: (
            <Trans
              components={[
                <a
                  style={{ textDecoration: 'underline' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    setTreModalVisible(true);
                  }}
                />
              ]}
            >
              {t(`contracts.form.${fieldName}`)}
            </Trans>
          ),
          valuePropName: 'checked',
          withDivider: true,
          layout: preRequisitesLayout,
          input: <PreRequisitesSwitch />,
          step: 1
        })),
    [isMRI, isPNO]
  );

  const displayEstimationCard = useMemo(
    () =>
      exemptions?.length > 0 ||
      (addressIsValid &&
        estimatedCost?.total_monthly_customer > 0 &&
        !estimatedCost.error),
    [estimatedCost, estimatedCost?.error, exemptions]
  );

  const filteredOptionnalGuarantees = useMemo(
    () =>
      optionnalGuarantees.filter(({ value }) =>
        isPNO ? value !== MRIWKOptionnalGuaranteesMap.machine_breakdown : true
      ),
    [isPNO]
  );

  const comparisonTableDataSource = useMemo(() => {
    if (isPNO) {
      return PNOguaranteePackageComparisonDataSource;
    }

    return MRIguaranteePackageComparisonDataSource;
  }, [isPNO, isMRI]);

  useEffect(() => {
    form.setFields([
      {
        name: ['building_type'],
        value: dataModal?.building_type,
        touched: false
      }
    ]);
  }, [dataModal?.building_type]);

  useEffect(() => {
    if (addressIsValid) {
      setEstimationAvoider(false);
    } else {
      setEstimatedCost(0);
      setEstimationAvoider('mriwk');
    }
  }, [addressIsValid]);

  useEffect(() => {
    const postal_code = form.getFieldValue(['address', 'postal_code']);

    if (typeof postal_code === 'string' && postal_code.length >= 5) {
      getWakamCities(postal_code);
    } else {
      setCities([]);
    }
  }, [postalCodeWatcher]);

  useEffect(() => {
    const oldAddress = formValues?.address;
    const oldCity = oldAddress?.city || '';

    const normalizedCity = removeAccents(oldCity || '')
      .replace('-', '')
      .replace(' ', '')
      .toUpperCase();

    const found = cities.find((city) => {
      const normalizedCityToCompare = removeAccents(city?.value)
        .replace('-', '')
        .replace(' ', '')
        .toUpperCase();

      return normalizedCityToCompare === normalizedCity;
    });

    setFormValues({
      ...formValues,
      address: {
        ...oldAddress,
        city: found?.value || cities[0]?.value || null
      }
    });

    form.setFieldValue('address', {
      ...oldAddress,
      city: found?.value || cities[0]?.value || null
    });
  }, [cities]);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (!manualAddress && addressAutocompleteRef.current) {
      addressAutocompleteRef.current.clear();
    }
  }, [manualAddress]);

  useEffect(() => {
    if (isInitialized) {
      return;
    }

    if (!isEdit) {
      setIsInitialized(true);
      return;
    }

    if (formValues?._id) {
      if (addressValidator(formValues?.address)) {
        setManualAddress(true);
      }

      if (formValues?.address?.additional) {
        setAddressAdditional(true);
      }

      setIsInitialized(true);
    }
  }, [formValues]);

  useEffect(() => {
    if (!formValues) {
      return;
    }

    const {
      subscriber = 'LEGAL_PERSON',
      subscriber_type = 'SCI',
      property_managers = propertyManagersMap.MONO
    } = formValues;

    let newPropertyManagersValue = propertyManagersMap.MONO;

    if (subscriber === 'LEGAL_PERSON') {
      newPropertyManagersValue = propertyManagersMap.MONO;
    } else if (subscriber === 'LEGAL_ENTITY') {
      if (subscriber_type === 'SCI') {
        newPropertyManagersValue = propertyManagersMap.SYNDYC_VOLUNTEER;
      } else if (subscriber_type === 'SYNDIC_PRO') {
        newPropertyManagersValue = propertyManagersMap.SYNDIC_PRO;
      } else if (subscriber_type === 'SYNDIC_VOLUNTEER') {
        newPropertyManagersValue = propertyManagersMap.SYNDYC_VOLUNTEER;
      } else if (subscriber_type === 'COMPANY') {
        newPropertyManagersValue = propertyManagersMap.COMPANY;
      }
    }

    if (newPropertyManagersValue !== property_managers) {
      setFormValues({
        ...formValues,
        property_managers: newPropertyManagersValue
      });
      form.setFieldValue('property_managers', newPropertyManagersValue);
    }
  }, [formValues]);

  useEffect(() => {
    if (surfaceFieldInitialized || type !== 'mriwk') {
      return;
    }

    if (
      !dataModal?.surface_area ||
      formValues?.surface_area === dataModal?.surface_area
    ) {
      setSurfaceFieldInitialized(true);
    }
  }, [dataModal?.surface_area, formValues?.surface_area]);

  useEffect(() => {
    if (dataModal?.manual_address) {
      setManualAddress(true);
    }
  }, [dataModal?.manual_address]);

  useEffect(() => {
    if (currentStep !== 0 || !surfaceFieldInitialized || type !== 'mriwk') {
      return;
    }

    let newSurfaceArea = MRIWK_MRI_SURFACE_DEFAULT_VALUE;

    if (isPNO) {
      newSurfaceArea = MRIWK_PNO_SURFACE_DEFAULT_VALUE;
    }

    if (newSurfaceArea !== formValues?.surface_area) {
      setFormValues({
        ...formValues,
        surface_area: newSurfaceArea
      });
      form.setFieldValue('surface_area', newSurfaceArea);

      if (form?.isFieldTouched('surface_area')) {
        antMessage.warning(t('message.mriwk.surface_update'));
      }
    }
  }, [formValues?.building_type]);

  useEffect(() => {
    if (!isBuildingTypeTouched || !isInitialized || type !== 'mriwk') {
      return;
    }

    let newGuaranteePackage = guaranteePackages[1].value;

    if (pnoBuildingTypes.includes(formValues?.building_type)) {
      newGuaranteePackage = guaranteePackages[2].value;
    }

    setFormValues({
      ...formValues,
      guarantee_package: newGuaranteePackage
    });
    form.setFieldValue('guarantee_package', newGuaranteePackage);
  }, [formValues?.building_type]);

  useEffect(() => {
    if (isBuildingTypeTouched) {
      return;
    }

    if (!dataModal?.guarantee_package || type !== 'mriwk') {
      return;
    }

    setFormValues({
      ...formValues,
      guarantee_package: dataModal?.guarantee_package
    });
    form.setFieldValue('guarantee_package', dataModal?.guarantee_package);
  }, [formValues?.building_type]);

  useEffect(() => {
    if (currentStep > 0) {
      setManualAddress(true);
    }
  }, [currentStep]);

  const getWakamCities = async (postalCode) => {
    try {
      const { data: cities } = await dispatchAPI('GET', {
        url: `/wakam/cities?postal_code=${postalCode}`
      });

      const mappedCities = cities.map((city) => ({
        value: city,
        label: city
      }));

      setCities(mappedCities || []);
    } catch (error) {
      message(error.response.status);
    }
  };

  const handleAddressChange = (selected) => {
    if (!form) {
      return;
    }

    // delete selected.city;
    const oldAddress = form.getFieldValue('address');

    setFormValues({
      ...formValues,
      address: {
        ...oldAddress,
        ...selected
      }
    });

    form.setFieldValue('address', {
      ...oldAddress,
      ...selected
    });
  };

  const handleAddressClear = () => {
    if (!form) {
      return;
    }

    const oldAddress = form.getFieldValue('address');
    setFormValues({
      ...formValues,
      address: {
        ...oldAddress,
        address_autocomplete: null,
        number: null,
        street: null,
        postal_code: null,
        country: null,
        city: null
      }
    });

    form.setFieldValue('address', {
      ...oldAddress,
      address_autocomplete: null,
      number: null,
      street: null,
      postal_code: null,
      country: null,
      city: null
    });
  };

  const fields = [
    {
      name: ['address', 'address_autocomplete'],
      rules: [
        { required: !manualAddress },
        {
          validator: async (_, value) => {
            const postalCode = formValues?.address?.postal_code || '';
            const isValid = isValidZipcode(postalCode);
            const isMetropolitan = isMetropolitanZipcode(postalCode);

            if (!isValid) {
              return Promise.reject("Ce code postal n'est pas valide");
            }
            if (!isMetropolitan) {
              return Promise.reject("Ce code postal n'est pas éligible");
            }
            return Promise.resolve();
          }
        }
      ],
      label: t('contracts.form.address.address_autocomplete'),
      formattedLabel: true,
      input: (
        <AddressAutocomplete
          ref={addressAutocompleteRef}
          onSelect={handleAddressChange}
          onClear={handleAddressClear}
          withAddressPreview
          disabled={manualAddress}
        />
      ),
      step: 0
    },
    {
      prerenderedItem: (
        <Space orientation="vertical">
          <Checkbox
            checked={manualAddress}
            onChange={(e) => setManualAddress(e.target.checked)}
          >
            {t('contracts.form.manual_address')}
          </Checkbox>

          <Checkbox
            checked={addressAdditional}
            onChange={(e) => setAddressAdditional(e.target.checked)}
          >
            {t('contracts.form.address_additional')}
          </Checkbox>
        </Space>
      ),
      step: 0
    },
    {
      name: ['address', 'number'],
      rules: [{ required: true, message: t('errors.form.REQUIRED') }],
      label: t('contracts.form.address.number'),
      formattedLabel: true,
      input: <InputNumber stringMode />,
      hidden: !manualAddress,
      step: 0
    },
    {
      name: ['address', 'street'],
      rules: [{ required: true, message: t('errors.form.REQUIRED') }],
      label: t('contracts.form.address.street'),
      formattedLabel: true,
      input: <Input />,
      hidden: !manualAddress,
      step: 0
    },
    {
      name: ['address', 'additional'],
      label: t('contracts.form.address.additional'),
      formattedLabel: true,
      input: <Input />,
      hidden: !addressAdditional,
      step: 0
    },
    {
      name: ['address', 'postal_code'],
      rules: [
        { required: true, message: t('errors.form.REQUIRED') },
        {
          validator: async (_, value) => {
            if (isValidZipcode(value) && !isMetropolitanZipcode(value)) {
              return Promise.reject("Ce code postal n'est pas éligible");
            }

            return Promise.resolve();
          }
        }
      ],
      label: t('contracts.form.address.postal_code'),
      formattedLabel: true,
      input: <Input style={{ width: '90px' }} autoComplete="off" />,
      help: exemptions.includes('administrative_area_level_2')
        ? t('help.exemptions')
        : null,
      hidden: !manualAddress,
      step: 0
    },
    {
      name: ['address', 'country'],
      rules: [{ required: true, message: t('errors.form.REQUIRED') }],
      label: t('contracts.form.address.country'),
      formattedLabel: true,
      initialValue: 'France',
      input: <Input disabled style={{ width: '90px' }} />,
      hidden: true,
      step: 0
    },
    {
      name: ['address', 'city'],
      trigger: '',
      rules: [{ required: true, message: t('errors.form.REQUIRED') }],
      label: t('contracts.form.address.city'),
      formattedLabel: true,
      input: <Select showSearch options={cities} />,
      help: exemptions.includes('address.city') ? t('help.exemptions') : null,
      step: 0
    },

    {
      name: ['building_count'],
      rules: [{ required: true }],
      input: <InputNumber min={1} />,
      help: exemptions.includes('building_count') ? t('help.exemptions') : null,
      step: 0,
      tooltip: true
    },
    {
      name: ['building_type'],
      rules: [{ required: true }],
      input: (
        <Form.Item name="building_type" noStyle>
          <Radio.Group
            buttonStyle="solid"
            className="radio-with-icon radio-with-icon-text-wrap radio-with-icon-row-wrap"
          >
            {buildingTypes.map(({ value, icon, label }) => (
              <Radio.Button value={value}>
                {icon}
                {label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>
      ),
      help: exemptions.includes('building_type') ? t('help.exemptions') : null,
      step: 0
    },
    ...arrayConditionnalElement(
      {
        name: ['building_type_other'],
        rules: [{ required: true }],
        input: <Input />,
        step: 0
      },
      formValues?.building_type === 'OTHER'
    ),
    ...arrayConditionnalElement(
      {
        name: ['building_quality'],
        rules: [{ required: true }],
        input: (
          <Form.Item name="building_quality" noStyle>
            <Radio.Group
              buttonStyle="solid"
              className="radio-with-icon radio-with-icon-text-wrap radio-with-icon-row-wrap"
            >
              {buildingQualities.map(({ value, icon, label }) => (
                <Radio.Button value={value}>
                  {icon}
                  {label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>
        ),
        help: exemptions.includes('building_type')
          ? t('help.exemptions')
          : null,
        step: 0
      },
      isMRI
    ),
    ...arrayConditionnalElement(
      {
        name: ['property_nature'],
        rules: [{ required: true }],
        input: (
          <FormRadioButtonWithCustom
            options={isPNO ? pnoPropertyNatures : mriPropertyNatures}
          />
        ),
        tooltip: true,
        help: exemptions.includes('property_nature')
          ? t('help.exemptions')
          : null,
        step: 0
      },
      isPNO && formValues?.building_type !== 'APARTMENT'
    ),
    {
      name: ['surface_area'],
      rules: [{ required: true }],
      input: (
        <InputNumber
          formatter={inputNumberFormatter}
          parser={inputNumberParser}
        />
      ),
      help: exemptions.includes('surface_area') ? t('help.exemptions') : null,
      label: (
        <div style={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
          {t('contracts.form.mriwk_surface_area')}
          <span style={{ color: 'grey' }}>
            {t('contracts.form.mriwk_surface_area_helper')}
          </span>
        </div>
      ),
      formattedLabel: true,
      step: 0
    },
    ...arrayConditionnalElement(
      [
        {
          name: ['contains_professional_activity'],
          rules: [{ required: true }],
          input: <PreRequisitesSwitch />,
          help: exemptions.includes('contains_professional_activity')
            ? t('help.exemptions')
            : null,
          valuePropName: 'checked',
          step: 0
        },
        ...arrayConditionnalElement(
          [
            {
              name: ['office_activity_percentage'],
              rules: [{ required: true }],
              input: <InputNumber addonAfter="%" min={0} max={100} />,
              help: exemptions.includes('office_activity_percentage')
                ? t('help.exemptions')
                : null,
              step: 0
            },
            {
              name: ['shop_activity_percentage'],
              rules: [{ required: true }],
              input: <InputNumber addonAfter="%" min={0} max={100} />,
              help: exemptions.includes('shop_activity_percentage')
                ? t('help.exemptions')
                : null,
              step: 0
            }
          ],
          formValues?.contains_professional_activity
        )
      ],
      isMRI
    ),
    ...arrayConditionnalElement(
      {
        name: ['is_condominium'],
        rules: [{ required: true }],
        input: <PreRequisitesSwitch />,
        help: exemptions.includes('is_condominium')
          ? t('help.exemptions')
          : null,
        valuePropName: 'checked',
        step: 0
      },
      formValues?.building_type === 'APARTMENT'
    ),
    {
      name: ['wood_construction_prerequisite'],
      rules: [{ required: true }],
      input: <PreRequisitesSwitch />,
      help: exemptions.includes('wood_construction_prerequisite')
        ? t('help.exemptions')
        : null,
      valuePropName: 'checked',
      step: 0
    },
    ...arrayConditionnalElement(
      [
        {
          name: ['wood_full_risk_prerequisite'],
          rules: [{ required: true }],
          input: <PreRequisitesSwitch />,
          help: exemptions.includes('wood_full_risk_prerequisite')
            ? t('help.exemptions')
            : null,
          valuePropName: 'checked',
          step: 0
        }
      ],
      formValues?.wood_construction_prerequisite
    ),
    {
      name: ['is_vacant'],
      rules: [{ required: true }],
      input: <PreRequisitesSwitch />,
      help: exemptions.includes('is_vacant') ? t('help.exemptions') : null,
      valuePropName: 'checked',
      step: 0
    },
    ...arrayConditionnalElement(
      [
        {
          name: ['vacant_reason'],
          rules: [{ required: true }],
          input: (
            <FormRadioButtonWithCustom
              options={vacantReason}
              buttonsStyle={{ width: 130 }}
              withIcon
              noStyle
            />
          ),
          help: exemptions.includes('vacant_reason')
            ? t('help.exemptions')
            : null,
          step: 0
        },
        ...arrayConditionnalElement(
          [
            {
              name: ['deadline_before_rental'],
              label: t('contracts.form.deadline_before_rental.title'),
              formattedLabel: true,
              rules: [{ required: true }],
              input: (
                <FormRadioButtonWithCustom options={deadline_before_rental} />
              ),
              help: exemptions.includes('deadline_before_rental')
                ? t('help.exemptions')
                : null,
              step: 0
            }
          ],
          formValues?.vacant_reason === 'EMPTY'
        ),
        {
          name: ['vacant_area'],
          label: t('contracts.form.vacant_area.title'),
          formattedLabel: true,
          rules: [{ required: true }],
          input: <FormRadioButtonWithCustom options={vacantArea} />,
          help: exemptions.includes('vacant_reason')
            ? t('help.exemptions')
            : null,
          step: 0
        }
      ],
      formValues?.is_vacant
    ),
    // ...arrayConditionnalElement(
    //   {
    //     name: ['contains_outbuilding'],
    //     rules: [{ required: true }],
    //     input: <PreRequisitesSwitch />,
    //     help: exemptions.includes('contains_outbuilding')
    //       ? t('help.exemptions')
    //       : null,
    //     valuePropName: 'checked',
    //     step: 0
    //   },
    //   isPNO
    // ),
    // ...arrayConditionnalElement(
    //   {
    //     name: ['outbuilding_surface'],
    //     rules: [{ required: true }],
    //     input: (
    //       <FormRadioButtonWithCustom
    //         options={outbuildingSurfaceSteps}
    //         getIsCustom={({ value }) =>
    //           value > MRIWK_OUTBUILDING_EXEMPTION_LIMIT &&
    //           formValues?.outbuilding_surface >
    //             MRIWK_OUTBUILDING_EXEMPTION_LIMIT
    //         }
    //         customInputProps={{
    //           min: MRIWK_OUTBUILDING_EXEMPTION_LIMIT + 1,
    //           max: null
    //         }}
    //       />
    //     ),
    //     help: exemptions.includes('outbuilding_surface')
    //       ? t('help.exemptions')
    //       : null,
    //     step: 0
    //   },
    //   isPNO && formValues?.contains_outbuilding
    // ),
    {
      name: ['is_furnished'],
      rules: [{ required: true }],
      noStyle: true,
      input: (
        <Radio.Group buttonStyle="solid" className="radio-with-icon">
          <Radio.Button value="NO">
            <UnfurnishedIcon />
            {t('contracts.tags.UNFURNISHED')}
          </Radio.Button>
          <Radio.Button value="YES">
            <FurnishedIcon />
            {t('contracts.tags.FURNISHED')}
          </Radio.Button>
          {/* <Radio.Button value="OTHER">
            <OtherIcon />
            {t('contracts.tags.OTHER')}
          </Radio.Button> */}
        </Radio.Group>
      ),
      help: exemptions.includes('is_furnished') ? t('help.exemptions') : null,
      step: 0
    },
    ...arrayConditionnalElement(
      {
        name: ['furniture_guarantee'],
        rules: [{ required: true }],
        input: (
          <FormRadioButtonWithCustom
            options={furnitureGuaranteeSteps}
            getIsCustom={({ value }) =>
              value > MRIWK_FURNITURE_GUARANTEE_EXEMPTION_LIMIT &&
              formValues?.furniture_guarantee >
                MRIWK_FURNITURE_GUARANTEE_EXEMPTION_LIMIT
            }
            customInputProps={{
              min: MRIWK_FURNITURE_GUARANTEE_EXEMPTION_LIMIT + 1,
              max: null
            }}
          />
        ),
        help: exemptions.includes('furniture_guarantee')
          ? t('help.exemptions')
          : null,
        step: 0
      },
      formValues?.is_furnished === 'YES'
    ),
    ...arrayConditionnalElement(
      {
        name: ['other_furnished'],
        rules: [{ required: true }],
        input: <Input />,
        help: exemptions.includes('other_furnished')
          ? t('help.exemptions')
          : null,
        step: 0
      },
      formValues?.is_furnished === 'OTHER'
    ),
    {
      name: ['optionnal_guarantees'],
      input: (
        <FormRadioButtonWithCustom
          options={filteredOptionnalGuarantees}
          withIcon
          multiple
        />
      ),
      valuePropName: 'checked',
      step: 0
    },
    {
      name: ['deductible_amount_ratio'],
      rules: [{ required: true }],
      input: <Select options={deductibleAmountRatios} style={{ width: 200 }} />,
      step: 0
    },
    {
      name: ['guarantee_package'],
      rules: [{ required: true }],
      layout: {
        labelCol: { span: 0 },
        wrapperCol: { xs: 24, sm: 24, md: 24, lg: 24, xl: 24 }
      },
      input: <ComparisonTable dataSource={comparisonTableDataSource} />,
      warningSublabel:
        isPNO && formValues?.guarantee_package !== 'PKG_3'
          ? t('contracts.show.mriwk.guarantee_package_pno_warning')
          : null,
      step: 0
    },

    {
      name: ['company_been_insured'],
      rules: [{ required: true }],
      input: <PreRequisitesSwitch />,
      valuePropName: 'checked',
      label: 'building_been_insured',
      step: 0
    },
    ...arrayConditionnalElement(
      [
        {
          name: ['last_company_name'],
          input: <Input style={{ width: 500 }} />,
          rules: [{ required: true }],
          step: 0
        },
        {
          name: ['last_contract_number'],
          input: <Input style={{ width: 500 }} />,
          step: 0
        },
        {
          name: ['last_termination_date'],
          input: <DatePicker format="DD/MM/YYYY" style={{ minWidth: 200 }} />,
          step: 0
        },
        {
          name: ['termination_by_company'],
          rules: [{ required: true }],
          input: <PreRequisitesSwitch />,
          valuePropName: 'checked',
          tooltip: true,
          step: 0
        }
      ],
      formValues?.company_been_insured
    ),
    ...arrayConditionnalElement(
      {
        name: ['termination_reason_by_company'],
        rules: [{ required: true }],
        input: (
          <Select style={{ width: 220 }}>
            {mriwkTerminationReasons.map(({ value, label }) => (
              <Select.Option value={value} key={value}>
                <Row justify="space-between" align="middle">
                  {label}
                  {/* <Tooltip
                    title={t(`contracts.form.tooltip.${e}`)}
                    overlayInnerStyle={{ width: 500 }}
                  >
                    <QuestionCircleOutlined style={{ float: 'right' }} />
                  </Tooltip> */}
                </Row>
              </Select.Option>
            ))}
          </Select>
        ),
        help: exemptions.includes('termination_by_company')
          ? t('help.exemptions')
          : null,
        step: 0
      },
      formValues?.termination_by_company
    ),
    ...arrayConditionnalElement(
      {
        name: ['termination_reason_by_company_custom'],
        rules: [{ required: true }],
        input: (
          <Input
            value={formValues?.termination_reason_by_company_custom}
            onChange={(value) =>
              setFormValues({
                ...formValues,
                termination_reason_by_company_custom: value
              })
            }
            style={{ width: 250 }}
          />
        ),
        step: 0
      },
      formValues?.termination_reason_by_company === 'OTHER'
    ),
    ...arrayConditionnalElement(
      {
        name: ['termination_date_by_company'],
        input: (
          <DatePicker
            format="DD/MM/YYYY"
            style={{ minWidth: 200 }}
            disabledDate={(current) => current > moment()}
          />
        ),
        step: 0
      },
      formValues?.termination_by_company
    ),
    {
      name: ['number_claims'],
      tooltip: true,
      tooltipLabel: t('contracts.form.tooltip.rcpmk.number_claims', {
        threshold: MRIWK_CLAIM_AMOUNT_EXEMPTION_THRESHOLD
      }),
      rules: [{ required: true }],
      input: <InputNumber min={0} />,
      help: exemptions.includes('number_claims') ? t('help.exemptions') : null,
      step: 0
    },
    ...arrayConditionnalElement(
      {
        name: ['claims'],
        noLabel: false,
        step: 0,
        help: exemptions.includes(`claim_amount`) ? t('help.exemptions') : null,
        input: (
          <Form.List name="claims">
            {(fields) => (
              <div>
                {fields.map((field) => (
                  <Space
                    key={field.key}
                    style={{ display: 'flex', marginBottom: 8 }}
                    align="baseline"
                  >
                    <Form.Item
                      noStyle
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...field}
                      name={[field.name, 'claim_date']}
                      key={[field.fieldKey, 'claim_date']}
                      rules={[
                        {
                          required: true,
                          message: 'Date du sinistre manquant'
                        }
                      ]}
                    >
                      <DatePicker
                        format="DD/MM/YYYY"
                        style={{ minWidth: 200 }}
                        disabledDate={(current) => {
                          return current >= moment();
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...field}
                      name={[field.name, 'claim_type']}
                      key={[field.fieldKey, 'claim_type']}
                      rules={[
                        {
                          required: true,
                          message: 'Type du sinistre manquant'
                        }
                      ]}
                    >
                      <Select
                        style={{ width: '200px' }}
                        placeholder="Type de sinistre"
                        options={claimTypes}
                      />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...field}
                      name={[field.name, 'claim_amount']}
                      key={[field.fieldKey, 'claim_amount']}
                      rules={[
                        {
                          required: true,
                          message: 'Montant du sinistre manquant'
                        }
                      ]}
                    >
                      <InputNumber
                        min={0}
                        style={{ width: '200px' }}
                        formatter={(value) => formatPrice(value, true, false)}
                        parser={(value) => numeral(value).value()}
                        placeholder="Montant du sinistre"
                      />
                    </Form.Item>
                  </Space>
                ))}
              </div>
            )}
          </Form.List>
        )
      },
      formValues?.number_claims >= 1
    ),
    {
      name: ['fraction_type'],
      rules: [{ required: true }],
      tooltip: false,
      input: (
        <Radio.Group buttonStyle="solid">
          <Radio.Button value="MONTHLY">
            {t('contracts.form.options.MONTHLY')}
          </Radio.Button>
          <Radio.Button value="QUARTERLY">
            {t('contracts.form.options.QUARTERLY')}
          </Radio.Button>
          <Radio.Button value="BIANNUAL">
            {t('contracts.form.options.BIANNUAL')}
          </Radio.Button>
          <Radio.Button value="YEARLY">
            {t('contracts.form.options.YEARLY')}
          </Radio.Button>
        </Radio.Group>
      ),
      step: 0,
      show: !amendmentPurposes.includes(purpose)
    },
    {
      name: ['payment_type'],
      label: 'payment_type',
      rules: [{ required: true }],
      input: (
        <Radio.Group buttonStyle="solid">
          <Radio.Button value="DIRECTDEBIT">
            {t('contracts.form.options.DIRECTDEBIT')}
          </Radio.Button>
          {formValues && formValues.fraction_type !== 'MONTHLY' && (
            <Radio.Button value="CARD">
              {t('contracts.form.options.CARD')}
            </Radio.Button>
          )}
        </Radio.Group>
      ),
      tooltip: true,
      step: 0,
      show: !amendmentPurposes.includes(purpose)
    },
    {
      prerenderedItem: (
        <MriwkTREModal
          open={treModalVisible}
          onCancel={() => setTreModalVisible(false)}
        />
      ),
      step: 1
    }
  ];

  return {
    mriwkFields: [...fields, ...prerequisiteFields],
    displayEstimationCard
  };
};

export default useMRIWKFields;
